import type { EventHandlerCallback } from "../engines/coreComponents/types";
import { DragType } from "../engines/interactionEngine/interactionObserverConstants";
import type { NodeStyle } from "../engines/layoutEngine/layout.types";
import type { FSC } from "../engines/renderingEngine/fsc.types";
import {
  BoxNode,
  IconNode,
  TextNode
} from "../engines/renderingEngine/primitiveNodes";
import { HORIZONTAL_DROP_INDICATOR_HEIGHT } from "./HorizontalDropIndicator";
import { PALETTE, THEME } from "./colorPalette.constants";
import { FieldIcons } from "./fieldNode.constants";
import { styles } from "./scomponents.styles";
import { getFieldBodyMaxLength, getFieldMaxLength } from "./scomponents.utils";

type FieldNodeProps = {
  title: string;
  key: string;
  selected: boolean;
  type: string;
  onDropped: EventHandlerCallback;
  onDragStart: EventHandlerCallback;
  onDropping: EventHandlerCallback;
  onDroppingOut: EventHandlerCallback;
  onClick: EventHandlerCallback;
  stepId: string; // Remove when DS-4766 is resolved
  swimlaneId: string; // Remove when DS-4766 is resolved
  theme: THEME;
  styleOverride: Partial<NodeStyle>;
  readonly: boolean;
  upStreamRulesCount: number;
  downStreamRulesCount: number;
  onUpStreamRuleIndicatorClick: () => void;
  onDownStreamRuleIndicatorClick: () => void;
  isRequired: boolean;
  isDroppingAbove?: boolean;
  isHidden: boolean;
  isObjectBackedField: boolean;
  objectFieldTypeIcon?: string;
};

const DROP_TYPES = [DragType.FIELD, DragType.NEW_FIELD];

export const FieldNode: FSC<FieldNodeProps> = ({
  key,
  selected,
  title,
  type,
  onDropped,
  onDropping,
  onDroppingOut,
  onDragStart,
  onClick,
  stepId,
  swimlaneId,
  theme = THEME.LIGHT,
  styleOverride,
  readonly,
  upStreamRulesCount,
  downStreamRulesCount,
  onUpStreamRuleIndicatorClick,
  onDownStreamRuleIndicatorClick,
  isRequired,
  isDroppingAbove,
  isHidden,
  isObjectBackedField,
  objectFieldTypeIcon
}) => {
  const style = styles[theme].field;
  const palette = PALETTE[theme];

  /**
   * These are props only applicable when we're NOT in readonly mode.
   */
  const nonReadonlyProps: Partial<Parameters<typeof BoxNode>[0]> = readonly
    ? {}
    : {
        dragType: DragType.FIELD,
        dropTypes: DROP_TYPES,
        dragHandleFor: key,
        eventHandlers: {
          onDropped,
          onDragStart,
          onDropping,
          onDroppingOut
        }
      };

  const upstreamIndicator = BoxNode({
    key: `${key}-UpStreamRuleIndicatorContainer`,
    style: { ...style.ruleIndicatorContainer, marginRight: 5 },
    eventHandlers: {
      onClick: onUpStreamRuleIndicatorClick
    },
    children: [
      IconNode({
        key: `${key}-UpStreamRuleIndicatorIcon`,
        icon: "ArrowUpLight",
        size: 15,
        color: palette.RULE_INDICATOR_UPSTREAM_ICON_COLOR,
        children: []
      }),
      TextNode({
        key: `${key}-UpStreamRuleIndicatorText`,
        text: upStreamRulesCount.toString(),
        textStyle: style.ruleIndicatorTitle,
        children: []
      })
    ]
  });

  const downstreamIndicator = BoxNode({
    key: `${key}-DownStreamRuleIndicatorContainer`,
    style: style.ruleIndicatorContainer,
    eventHandlers: {
      onClick: onDownStreamRuleIndicatorClick
    },
    children: [
      IconNode({
        key: `${key}-DownStreamRuleIndicatorIcon`,
        icon: "ArrowDownLight",
        size: 15,
        color: palette.RULE_INDICATOR_DOWNSTREAM_ICON_COLOR,
        children: []
      }),
      TextNode({
        key: `${key}-DownStreamRuleIndicatorText`,
        text: downStreamRulesCount.toString(),
        textStyle: style.ruleIndicatorTitle,
        children: []
      })
    ]
  });

  const requiredIndicator = [];
  const ruleIndicators = [];

  if (upStreamRulesCount > 0) {
    ruleIndicators.push(upstreamIndicator);
  }

  if (downStreamRulesCount > 0) {
    ruleIndicators.push(downstreamIndicator);
  }

  if (isRequired) {
    requiredIndicator.push(
      TextNode({
        key: `${key}-RequiredIndicator`,
        text: "*",
        textStyle: style.requiredIndicator,
        children: []
      })
    );
  }

  const fieldTextStyles = {
    ...style.titleNormal,
    ...getFieldMaxLength(ruleIndicators.length)
  };

  const fieldBodyStyles = {
    ...style.body,
    ...getFieldBodyMaxLength(ruleIndicators.length)
  };
  const fieldTypeIcon = isObjectBackedField
    ? objectFieldTypeIcon || "Puzzle"
    : FieldIcons[type as keyof typeof FieldIcons] || "Puzzle";
  const fieldNode = BoxNode({
    key: `${key}`,
    style: {
      ...(selected ? style.containerSelected : style.container),
      ...styleOverride,
      ...(isDroppingAbove
        ? {
            marginTop:
              (style.container.marginTop ?? 0) -
              HORIZONTAL_DROP_INDICATOR_HEIGHT
          }
        : {}),
      ...(isHidden
        ? {
            backgroundColor: isHidden
              ? palette.FIELD_HIDDEN_BACKGROUND_COLOR
              : palette.FIELD_BACKGROUND
          }
        : {})
    },
    ...nonReadonlyProps,
    eventHandlers: {
      ...nonReadonlyProps.eventHandlers,
      onClick
    },
    variables: {
      stepId,
      swimlaneId
    },
    children: [
      BoxNode({
        key: `${key}-FieldBody`,
        style: fieldBodyStyles,
        children: [
          IconNode({
            key: `${key}-Icon`,
            icon: fieldTypeIcon,
            size: 15,
            color: palette.FIELD_ICON,
            children: []
          }),
          TextNode({
            key: `${key}-Text`,
            text: title || "Untitled field ",
            textStyle: title ? fieldTextStyles : style.titleItalic,
            style: style.titleContainer,
            children: []
          }),
          BoxNode({
            key: `${key}-RequiredIndicatorWrapper`,
            style: {},
            children: requiredIndicator
          })
        ]
      }),
      BoxNode({
        style: styles.dark.ruleIndicatorsWrapper,
        key: `${key}-RuleIndicatorsWrapper`,
        children: ruleIndicators
      }),

      ...(isObjectBackedField
        ? [
            BoxNode({
              style: styles.dark.ruleIndicatorsWrapper,
              key: `${key}-ObjectBackedFieldIndicator`,
              children: [
                IconNode({
                  key: `${key}-isObjectBackedField`,
                  icon: "App",
                  size: 15,
                  color: palette.FIELD_ICON,
                  children: []
                })
              ]
            })
          ]
        : [])
    ]
  });

  return fieldNode;
};
